"use client";

import { Bot, Loader2, ChevronRight } from "lucide-react";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";
import type { BaseCarrier } from "./types";

export interface CarrierLoadingCardProps {
  carrier: BaseCarrier;
  /** The carrier asked questions and the Questions Agent is answering them. */
  agentAnswering?: boolean;
  /** Stops the agent on this carrier; the underwriter answers the questions instead. */
  onCancelAgent?: () => void;
}

export function CarrierLoadingCard({ carrier, agentAnswering = false, onCancelAgent }: CarrierLoadingCardProps) {
  return (
    <div
      className={cn(
        "@container relative bg-white rounded-lg border transition-all shadow-[0_1px_3px_rgba(0,0,0,0.06)] overflow-hidden",
        agentAnswering ? "border-violet-400" : "border-blue-200",
      )}
    >
      {/* shimmer effect overlay */}
      <div
        className={cn(
          "absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent to-transparent pointer-events-none",
          agentAnswering ? "via-violet-50/80" : "via-blue-50/80",
        )}
      />

      <div className="flex items-center gap-4 px-4 py-3 relative">
        {/* Checkbox spacer */}
        <div className="w-5 flex-shrink-0" />

        {/* Chevron placeholder */}
        <div className="flex-shrink-0 text-gray-300">
          <ChevronRight className="w-4 h-4" />
        </div>

        {/* Carrier logo — hidden when container is very narrow */}
        <div className="flex-shrink-0 w-14 h-10 hidden @3xl:flex items-center justify-center bg-gray-50 rounded-md p-1.5 border border-gray-100 opacity-50">
          <img
            src={carrier.logo}
            alt={carrier.name}
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              e.currentTarget.src = "/images/carriers/placeholder.svg";
            }}
          />
        </div>

        {/* Carrier name */}
        <div className="w-36 flex-shrink-0">
          <Typography as="span" variant="body" weight="semibold" truncate className="block">
            {carrier.name}
          </Typography>
        </div>

        {/* Loading premium placeholder */}
        <div className="w-36 flex-shrink-0">
          <div className="h-5 w-20 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Commission placeholder — hidden when container is narrow */}
        <div className="w-28 flex-shrink-0 hidden @4xl:flex justify-center">
          <div className="h-4 w-12 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Quote ID placeholder — hidden when container is narrow */}
        <div className="w-32 flex-shrink-0 hidden @5xl:block">
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Date placeholder — hidden when container is narrow */}
        <div className="w-24 flex-shrink-0 hidden @6xl:block">
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Alerts spacer */}
        <div className="w-12 flex-shrink-0 hidden @3xl:block" />

        {/* Loading badge */}
        <div className={cn("flex-shrink-0 flex justify-center ml-auto", agentAnswering ? "w-auto" : "w-24")}>
          {agentAnswering ? (
            <Typography as="span" variant="small" weight="medium" color="text-violet-700" className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 rounded-full whitespace-nowrap">
              <Loader2 className="w-3 h-3 animate-spin" />
              Answering questions
            </Typography>
          ) : (
            <Typography as="span" variant="small" weight="medium" color="text-blue-600" className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded-full">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading
            </Typography>
          )}
        </div>

        {/* Re-rate spacer */}
        <div className="w-10 flex-shrink-0 hidden @3xl:block" />
      </div>

      {agentAnswering && (
        <div className="relative flex items-start gap-2 border-t border-violet-100 bg-violet-50/60 px-4 py-2">
          <Bot className="mt-0.5 h-4 w-4 flex-shrink-0 text-violet-600" />
          <Typography as="p" variant="small" color="text-violet-800" className="flex-1">
            {carrier.name} asked underwriting questions. Our Questions Agent is answering
            them from the submission data and will re-rate automatically.
          </Typography>
          {onCancelAgent && (
            <button
              type="button"
              onClick={onCancelAgent}
              className="flex-shrink-0 rounded-md border border-violet-200 bg-white px-2 py-0.5 text-xs font-medium text-violet-700 hover:bg-violet-100"
            >
              {"I'll answer"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
